import { useEffect, useState } from 'react'
import { Plus, Star, Eye, EyeOff, Trash2, Search, Camera, X, MoreHorizontal } from 'lucide-react'
import FileUpload from '../../components/admin/FileUpload'
import { compressImage } from '../../lib/imageUtils'

interface Testimonial {
  _id: string
  studentName: string
  courseName: string
  rating: number
  feedback: string
  isApproved: boolean
  createdAt: string
  avatar?: string
  photo?: string
}

export default function AdminTestimonials() {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState({
    studentName: '',
    courseName: '',
    rating: 5,
    feedback: '',
    photo: ''
  })
  const [uploadingPhoto, setUploadingPhoto] = useState(false)

  useEffect(() => {
    fetchTestimonials()
  }, [])

  const fetchTestimonials = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/testimonials/admin', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setTestimonials(data.testimonials || [])
      }
    } catch (error) {
      console.error('Error fetching testimonials:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/testimonials/admin', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...formData, isApproved: true })
      })

      if (response.ok) {
        alert('Testimonial added successfully!')
        setShowForm(false)
        fetchTestimonials()
        setFormData({
          studentName: '',
          courseName: '',
          rating: 5,
          feedback: '',
          photo: ''
        })
      }
    } catch (error) {
      console.error('Error adding testimonial:', error)
      alert('Error adding testimonial')
    }
  }

  const toggleApproval = async (id: string, currentStatus: boolean) => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/testimonials/admin/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ isApproved: !currentStatus })
      })

      if (response.ok) {
        setTestimonials(testimonials.map(t =>
          t._id === id ? { ...t, isApproved: !currentStatus } : t
        ))
      }
    } catch (error) {
      console.error('Error updating testimonial:', error)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this testimonial?')) return

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/testimonials/admin/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        setTestimonials(testimonials.filter(t => t._id !== id))
        alert('Testimonial deleted successfully')
      }
    } catch (error) {
      console.error('Error deleting testimonial:', error)
      alert('Error deleting testimonial')
    }
  }

  const filteredTestimonials = testimonials.filter(testimonial =>
    testimonial.studentName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    testimonial.courseName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    testimonial.feedback.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const stats = {
    total: testimonials.length,
    approved: testimonials.filter(t => t.isApproved).length,
    pending: testimonials.filter(t => !t.isApproved).length,
    avgRating: testimonials.length > 0
      ? (testimonials.reduce((sum, t) => sum + t.rating, 0) / testimonials.length).toFixed(1)
      : 0
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-dark-800 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-dark-700">
          <p className="text-2xl font-bold text-charcoal-900 dark:text-white">{stats.total}</p>
          <p className="text-sm text-charcoal-500 dark:text-charcoal-400">Total</p>
        </div>
        <div className="bg-white dark:bg-dark-800 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-dark-700">
          <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{stats.approved}</p>
          <p className="text-sm text-charcoal-500 dark:text-charcoal-400">Approved</p>
        </div>
        <div className="bg-white dark:bg-dark-800 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-dark-700">
          <p className="text-2xl font-bold text-amber-500 dark:text-amber-400">{stats.pending}</p>
          <p className="text-sm text-charcoal-500 dark:text-charcoal-400">Pending</p>
        </div>
        <div className="bg-white dark:bg-dark-800 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-dark-700">
          <p className="text-2xl font-bold text-rose-600 dark:text-rose-400 flex items-center gap-1">
            <Star className="h-6 w-6 fill-rose-600 dark:fill-rose-400" />
            {stats.avgRating}
          </p>
          <p className="text-sm text-charcoal-500 dark:text-charcoal-400">Avg Rating</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white dark:bg-dark-800 rounded-3xl p-4 shadow-sm border border-gray-100 dark:border-dark-700">
        <div className="flex flex-col md:flex-row gap-4 justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-charcoal-400" />
            <input
              type="text"
              placeholder="Search testimonials..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all"
            />
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center justify-center gap-2 px-6 py-2.5 bg-rose-600 text-white rounded-xl hover:bg-rose-700 transition-all shadow-lg shadow-rose-600/20 hover:shadow-xl hover:shadow-rose-600/30"
          >
            <Plus className="h-5 w-5" />
            <span>Add Testimonial</span>
          </button>
        </div>
      </div>

      {/* Add Form */}
      {showForm && (
        <div className="bg-white dark:bg-dark-800 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-dark-700 animate-slide-up">
          <h2 className="text-xl font-bold text-charcoal-900 dark:text-white mb-6">
            Add New Testimonial
          </h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Photo Upload Section */}
            <div className="space-y-4">
              <label className="block text-sm font-semibold text-charcoal-700 dark:text-charcoal-300">
                Student Photo (Optional)
              </label>

              {formData.photo ? (
                <div className="relative inline-block">
                  <img
                    src={formData.photo}
                    alt="Student"
                    className="w-32 h-32 rounded-2xl object-cover border-2 border-gray-100 dark:border-dark-600"
                  />
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, photo: '' })}
                    className="absolute -top-2 -right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="max-w-md">
                  <FileUpload
                    uploadType="image"
                    multiple={false}
                    onUpload={async (files) => {
                      if (files.length > 0) {
                        try {
                          const compressedBase64 = await compressImage(files[0], 800, 800, 0.7)
                          setFormData({ ...formData, photo: compressedBase64 })
                        } catch (error) {
                          console.error('Error compressing image:', error)
                          const reader = new FileReader()
                          reader.onloadend = () => {
                            setFormData({ ...formData, photo: reader.result as string })
                          }
                          reader.readAsDataURL(files[0])
                        }
                      }
                    }}
                    onUrlAdd={(url) => setFormData({ ...formData, photo: url })}
                    placeholder="Upload student photo"
                    maxSize={5}
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-semibold text-charcoal-700 dark:text-charcoal-300 mb-2">
                  Student Name *
                </label>
                <input
                  type="text"
                  value={formData.studentName}
                  onChange={(e) => setFormData({ ...formData, studentName: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all"
                  placeholder="Student name"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-charcoal-700 dark:text-charcoal-300 mb-2">
                  Course Name *
                </label>
                <input
                  type="text"
                  value={formData.courseName}
                  onChange={(e) => setFormData({ ...formData, courseName: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all"
                  placeholder="Course name"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-charcoal-700 dark:text-charcoal-300 mb-2">
                  Rating *
                </label>
                <select
                  value={formData.rating}
                  onChange={(e) => setFormData({ ...formData, rating: parseInt(e.target.value) })}
                  required
                  className="w-full px-4 py-3 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all"
                >
                  {[5, 4, 3, 2, 1].map(num => (
                    <option key={num} value={num}>{num} Stars</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-charcoal-700 dark:text-charcoal-300 mb-2">
                Feedback *
              </label>
              <textarea
                value={formData.feedback}
                onChange={(e) => setFormData({ ...formData, feedback: e.target.value })}
                required
                rows={4}
                className="w-full px-4 py-3 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all"
                placeholder="Student feedback..."
              />
            </div>
            <div className="flex gap-4 pt-2">
              <button
                type="submit"
                className="px-6 py-2.5 bg-rose-600 text-white rounded-xl hover:bg-rose-700 transition-all shadow-lg shadow-rose-600/20"
              >
                Add Testimonial
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-6 py-2.5 border border-gray-200 dark:border-dark-600 rounded-xl hover:bg-gray-50 dark:hover:bg-dark-700 text-charcoal-600 dark:text-charcoal-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Testimonials List */}
      <div className="grid grid-cols-1 gap-4">
        {filteredTestimonials.map((testimonial) => (
          <div
            key={testimonial._id}
            className="group bg-white dark:bg-dark-800 rounded-3xl p-6 border border-gray-100 dark:border-dark-700 hover:shadow-xl transition-all duration-300 hover:border-rose-200 dark:hover:border-rose-900/30"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-4 mb-4">
                  {testimonial.photo ? (
                    <img
                      src={testimonial.photo}
                      alt={testimonial.studentName}
                      className="w-12 h-12 rounded-full object-cover border-2 border-rose-100 dark:border-rose-900/30"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-rose-100 to-rose-200 dark:from-rose-900/30 dark:to-rose-800/30 flex items-center justify-center">
                      <span className="text-rose-600 dark:text-rose-400 font-bold text-lg">
                        {testimonial.studentName.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div>
                    <h3 className="font-bold text-charcoal-900 dark:text-white group-hover:text-rose-600 transition-colors">
                      {testimonial.studentName}
                    </h3>
                    <p className="text-sm text-charcoal-500 dark:text-charcoal-400">
                      {testimonial.courseName}
                    </p>
                  </div>
                  <span className={`ml-auto px-2.5 py-1 text-xs font-bold rounded-lg backdrop-blur-md ${testimonial.isApproved
                    ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                    : 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400'
                    }`}>
                    {testimonial.isApproved ? 'Approved' : 'Pending'}
                  </span>
                </div>

                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`h-4 w-4 ${i < testimonial.rating
                        ? 'fill-rose-500 text-rose-500'
                        : 'text-gray-200 dark:text-dark-600'
                        }`}
                    />
                  ))}
                </div>

                <p className="text-charcoal-600 dark:text-charcoal-300 mb-6 italic leading-relaxed">
                  "{testimonial.feedback}"
                </p>

                <div className="flex items-center gap-2 pt-4 border-t border-gray-100 dark:border-dark-700">
                  <button
                    onClick={() => toggleApproval(testimonial._id, testimonial.isApproved)}
                    className={`px-4 py-2 rounded-xl transition-colors flex items-center gap-2 text-sm font-medium ${testimonial.isApproved
                      ? 'bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-900/20 dark:text-amber-400 dark:hover:bg-amber-900/30'
                      : 'bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400 dark:hover:bg-green-900/30'
                      }`}
                  >
                    {testimonial.isApproved ? (
                      <><EyeOff className="h-4 w-4" /> Hide</>
                    ) : (
                      <><Eye className="h-4 w-4" /> Approve</>
                    )}
                  </button>
                  <button
                    onClick={() => handleDelete(testimonial._id)}
                    className="px-4 py-2 bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30 rounded-xl transition-colors flex items-center gap-2 text-sm font-medium ml-auto"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredTestimonials.length === 0 && (
        <div className="bg-white dark:bg-dark-800 rounded-3xl p-12 border border-gray-100 dark:border-dark-700 text-center">
          <div className="w-20 h-20 bg-gray-50 dark:bg-dark-700 rounded-full flex items-center justify-center mx-auto mb-6">
            <Star className="h-10 w-10 text-charcoal-400" />
          </div>
          <h3 className="text-xl font-bold text-charcoal-900 dark:text-white mb-2">
            No testimonials found
          </h3>
          <p className="text-charcoal-500 dark:text-charcoal-400 mb-8">
            Add your first testimonial to get started
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="px-6 py-3 bg-rose-600 text-white rounded-xl hover:bg-rose-700 transition-colors shadow-lg shadow-rose-600/20"
          >
            Add Testimonial
          </button>
        </div>
      )}
    </div>
  )
}
